import type { PublicSentimentBtmTech, PublicSentimentEntry, PublicSentimentSection, SiteEvidenceDocument } from '../../types/siteEvidence';
import { hashCanonicalJson } from '../reportGeneration/reportContractHash';

export const PUBLIC_SENTIMENT_SCOPE_VERSION = 'pse-scope-v7-selected-collection-point';
export interface SentimentQueryContext {
  version: typeof PUBLIC_SENTIMENT_SCOPE_VERSION;
  fingerprint: string;
  mode: 'sed-build' | 'standalone-psr';
  windowDays: number;
  sourceOptions?: { includeTribalRecords?: boolean };
  techs: PublicSentimentBtmTech[];
  localityTerms: string[];
  stateTerms: string[];
  projectName: string;
}
export interface SentimentAssessment {
  method: 'dated-local-explicit-stance-v1';
  status: 'assessed' | 'insufficient-evidence';
  localObservationCount: number;
  unscoredObservationCount: number;
  supportIndex?: number;
  meanConfidence?: number;
  reason: string;
  monthly: Array<{ month: string; count: number; supportIndex: number }>;
}

/** Only actual intent selections participate. Undecided technologies are not selected. */
export function selectedSentimentTechs(sed: SiteEvidenceDocument | null | undefined): PublicSentimentBtmTech[] {
  const intent = sed?.fullProjectIntent;
  const selected = new Set<PublicSentimentBtmTech>(['general-datacenter']);
  const keys = (intent?.phases ?? []).flatMap(phase => [phase.powerSource, ...(phase.powerSourceConfig?.components ?? [])]);
  for (const key of keys) {
    if (['recip', 'gas-turbine', 'gas-cc', 'gas-sc'].includes(key)) selected.add('natural-gas');
    if (key === 'solar-bess' || key === 'battery') selected.add('solar-bess');
    if (key === 'wind-bess') selected.add('wind-bess');
    if (key === 'smr' || key === 'large-nuclear') selected.add('nuclear-smr');
  }
  if (intent?.nuclearInterest === 'primary' || intent?.nuclearInterest === 'considering') selected.add('nuclear-smr');
  // An explicit exclusion wins over stale phase aliases.
  if (intent?.nuclearInterest === 'no') selected.delete('nuclear-smr');
  return [...selected].sort();
}

export function sentimentQueryContext(sed: SiteEvidenceDocument | null, args: {
  mode?: 'sed-build' | 'standalone-psr'; windowDays?: number; techs?: readonly PublicSentimentBtmTech[];
  localityTerms?: string[]; stateTerms?: string[]; sourceOptions?: { includeTribalRecords?: boolean };
} = {}): SentimentQueryContext {
  const context: Omit<SentimentQueryContext, 'fingerprint'> = {
    version: PUBLIC_SENTIMENT_SCOPE_VERSION,
    mode: args.mode ?? 'sed-build',
    windowDays: Math.max(30, Math.min(1825, Number.isFinite(args.windowDays) ? Math.round(args.windowDays!) : 365)),
    sourceOptions: args.sourceOptions,
    techs: [...new Set(args.techs ?? selectedSentimentTechs(sed))].sort(),
    localityTerms: args.localityTerms ?? [], stateTerms: args.stateTerms ?? [], projectName: sed?.siteName ?? '',
  };
  return { ...context, fingerprint: hashCanonicalJson({ ...context,
    location: sed?.location ?? null, intent: sed?.fullProjectIntent ?? sed?.projectIntent ?? null,
    studyArea: sed?.studyAreaScope?.selectionHash ?? null,
  }) };
}

const TOPICS: Record<PublicSentimentBtmTech, RegExp> = {
  'general-datacenter': /\b(?:data[ -]?cent(?:er|re)s?|hyperscale|colocation)\b/i,
  'natural-gas': /\b(?:natural gas|gas[ -]fired|gas turbine|gas power|combined cycle|gas peaker)\b/i,
  'nuclear-smr': /\b(?:nuclear|small modular reactor|SMR|NRC|COLA)\b/i,
  'solar-bess': /\b(?:solar|photovoltaic|battery storage|battery energy storage|BESS)\b/i,
  'wind-bess': /\b(?:wind farm|wind turbine|wind project|offshore wind|onshore wind)\b/i,
};

export function sentimentTopicMatches(text: string, tech: PublicSentimentBtmTech): boolean {
  return TOPICS[tech].test(text);
}

/** Keep adjacent pronoun/speaker follow-ups so a mixed public position cannot
 * become unanimous merely because its second sentence omits the technology. */
export function sentimentTopicPassages(text: string, tech: PublicSentimentBtmTech): string[] {
  const sentences = text.split(/(?<=[.!?])\s+|\n+/).map(row => row.trim()).filter(Boolean);
  const selected = new Set<number>();
  for (let index = 0; index < sentences.length; index++) {
    if (!sentimentTopicMatches(sentences[index]!, tech)) continue;
    selected.add(index);
    for (let next = index + 1; next < Math.min(sentences.length, index + 3); next++) {
      const line = sentences[next]!;
      if (!/\b(?:it|this|that|proposal|project|plan|others|they)\b/i.test(line)
        || !/\b(?:residents?|citizens?|neighbou?rs?|others?|they|support|oppose|opposition|object|concern)\b/i.test(line)) break;
      selected.add(next);
    }
  }
  return [...selected].sort((a, b) => a - b).map(index => sentences[index]!);
}

/** Filter the discovery projection before source/total caps; retain a URL receipt. */
export function sentimentDiscoveryExclusion(entry: PublicSentimentEntry, context: SentimentQueryContext, now: string): string | undefined {
  if (!context.techs.includes(entry.btmTech) || !sentimentTopicMatches(entry.sourcePassage ?? entry.supportingSnippet, entry.btmTech)) return 'unrelated-to-selected-technology';
  const date = Date.parse(entry.observedDate ?? '');
  if (Number.isFinite(date)) {
    const age = (Date.parse(now) - date) / 86_400_000;
    if (age < -1 || age > context.windowDays) return entry.dateBasis === 'first-seen' ? 'first-seen-outside-window' : 'source-date-outside-window';
  }
  return undefined;
}
/** A hearing or unrelated council action is not an expression of project support/opposition. */
export function explicitProjectStance(text: string): number | undefined {
  // These state a neutral/non-objection position, not affirmative support.
  const neutral = /\b(?:neither support(?:s)? nor oppos(?:e|es)|neutral position|no objections?|no opposition)\b/i.test(text);
  const remaining = text.replace(/\b(?:neither support(?:s)? nor oppos(?:e|es)|neutral position|no objections?|no opposition)\b/gi, '')
    .replace(/\b(?:not|never|does not|do not|did not)\s+(?:support|oppose|endorse|approve)\w*\b/gi, '');
  const against = /\b(?:oppose[ds]?|opposition|protest(?:s|ed)?|object(?:ion|ions)|concerns?)\b/i.test(remaining);
  const support = /\b(?:support(?:s|ed|ive)?|endorse[ds]?|in favou?r)\b/i.test(remaining);
  if ((against && support) || (neutral && (against || support))) return undefined;
  if (neutral) return 0;
  return against ? -1 : support ? 1 : undefined;
}

function mentions(text: string, terms: string[]): boolean {
  return terms.some(term => term.trim().length >= 3 && text.toLowerCase().includes(term.trim().toLowerCase()));
}

/**
 * The fixed unscored reasons `scopeSentimentEntry` stores verbatim on SED
 * rows, keyed by the stable reason code the PSR ledger and charts use
 * (`publicSentimentReasonCodes.ts`). The strings are persisted source text:
 * changing one changes every stored scan's meaning, so they are frozen here
 * and classified by exact match at report time instead of being re-worded.
 * A caller-supplied `assessmentReason` (e.g. an OCR limitation) passes through
 * unchanged when only the confidence is unusable.
 */
export const SENTIMENT_UNSCORED_REASONS = Object.freeze({
  'not-selected-technology': 'Record does not identify a selected project technology.',
  truncated: 'The cited source passage is truncated; omitted statements may change the apparent public position.',
  'outside-speaker': 'The statement names another or unverified speaker locality; the municipal publisher does not establish local opinion.',
  'publisher-only': 'Municipal source lacks an explicit local project or speaker link; publisher jurisdiction alone does not establish local opinion.',
  undated: 'Source publication/event date is unknown; retrieval time is not an event date.',
  'first-seen': 'First-seen date does not establish the publication/event date within this window.',
  'agenda-only': 'An agenda identifies planned business; it does not establish actual resident testimony or a completed action.',
  'out-of-window': 'Source date is outside the requested observation window.',
  'policy-only': 'Agency or policy action is contextual evidence, not an expression of resident opinion.',
  ambiguous: 'A clear public stance is not established; mixed or ambiguous statements remain available as source context.',
  'no-usable-assessment': 'Source did not provide a usable stance assessment.',
} as const);
export type SentimentUnscoredReasonCode = keyof typeof SENTIMENT_UNSCORED_REASONS;

/** Preserve every source row; only the scoring projection is withheld when attribution is insufficient. */
export function scopeSentimentEntry(entry: PublicSentimentEntry, context: SentimentQueryContext, now: string): PublicSentimentEntry {
  const text = entry.sourcePassage ?? entry.supportingSnippet ?? '';
  const relevant = context.techs.includes(entry.btmTech) && TOPICS[entry.btmTech].test(text);
  const project = context.projectName.length >= 4 && mentions(text, [context.projectName]);
  const speakerText = text.replace(/\b(?:residents|citizens|neighbors|neighbours)\b/gi, word => word.toLowerCase());
  const namedSpeakers = [...speakerText.matchAll(/\b([A-Z][A-Za-z.'-]+(?:\s+[A-Z][A-Za-z.'-]+){0,3})\s+(?:residents|citizens|neighbors|neighbours)\b/g)]
    .map(match => match[1]!.replace(/^(?:The|Some|Other|Many|Local|Several|All|Most|Affected|Nearby|These|Those)\s+/i, ''))
    .filter(name => !/^(?:The|Some|Other|Many|Local|Several|All|Most|Affected|Nearby|These|Those)$/i.test(name));
  const outsideSpeaker = namedSpeakers.some(name => !mentions(name, context.localityTerms) && !mentions(context.projectName, [name]));
  // Publisher jurisdiction identifies the source, not the quoted project's or speakers' locality.
  const isLocal = !outsideSpeaker && (project || mentions(text, context.localityTerms));
  const scope = isLocal ? 'site' : mentions(text, context.stateTerms.filter(term => term.length > 2)) ? 'state' : 'country';
  const observed = Date.parse(entry.observedDate ?? '');
  const age = (Date.parse(now) - observed) / 86_400_000;
  const topicStatements = sentimentTopicPassages(text, entry.btmTech).join(' ');
  const publicVoice = /\b(?:residents?|citizens?|community|public commenters?|neighbou?rs?|opponents?|supporters?|petitioners?|local people)\b/i.test(topicStatements);
  const policyAction = /\b(?:permit|agency|council|commission|moratorium|ordinance|approval|approved|denied|ban)\b/i.test(topicStatements);
  const stance = publicVoice ? explicitProjectStance(topicStatements) : undefined;
  const R = SENTIMENT_UNSCORED_REASONS;
  let reason: string | undefined;
  if (!relevant) reason = R['not-selected-technology'];
  else if (entry.passageTruncated) reason = R.truncated;
  else if (outsideSpeaker) reason = R['outside-speaker'];
  else if (entry.scopeBasis === 'verified-local-source' && !isLocal) reason = R['publisher-only'];
  else if (!Number.isFinite(observed)) reason = R.undated;
  else if (entry.dateBasis === 'first-seen') reason = R['first-seen'];
  else if (entry.dateBasis === 'scheduled-event') reason = R['agenda-only'];
  else if (age < -1 || age > context.windowDays) reason = R['out-of-window'];
  else if (stance === undefined) reason = policyAction && !publicVoice ? R['policy-only'] : R.ambiguous;
  else if (!Number.isFinite(entry.confidence) || entry.confidence <= 0) reason = entry.assessmentReason ?? R['no-usable-assessment'];
  return { ...entry, sourceSentimentScore: entry.sourceSentimentScore ?? entry.sentimentScore, sourceConfidence: entry.sourceConfidence ?? entry.confidence, scope, scopeBasis: isLocal ? (entry.scopeBasis ?? 'named-locality') : 'regional-context',
    stanceKind: publicVoice ? 'public-opinion' : policyAction ? 'policy-action' : 'unknown',
    assessmentReason: reason,
    assessmentStatus: reason ? 'unscored' : 'assessed',
    // Topic polarity is explicit stance; article emotional tone does not establish community opinion.
    sentimentScore: reason ? 0 : stance!, confidence: reason ? 0 : Math.min(entry.confidence, 1),
  };
}

export function assessPublicSentiment(entries: readonly PublicSentimentEntry[]): SentimentAssessment {
  const local = entries.filter(row => row.scope === 'site' && row.assessmentStatus === 'assessed' && row.confidence > 0);
  const index = (rows: readonly PublicSentimentEntry[]) => Math.round(50 + 50 * rows.reduce((sum, row) => sum + row.sentimentScore * row.confidence, 0) / rows.reduce((sum, row) => sum + row.confidence, 0));
  const months = new Map<string, PublicSentimentEntry[]>();
  for (const row of local) {
    const month = row.observedDate!.slice(0, 7);
    months.set(month, [...(months.get(month) ?? []), row]);
  }
  return {
    method: 'dated-local-explicit-stance-v1', status: local.length ? 'assessed' : 'insufficient-evidence',
    localObservationCount: local.length, unscoredObservationCount: entries.filter(row => row.assessmentStatus !== 'assessed').length,
    ...(local.length ? { supportIndex: index(local), meanConfidence: local.reduce((sum, row) => sum + row.confidence, 0) / local.length } : {}),
    reason: local.length ? 'Confidence-weighted support among dated, explicitly expressed local observations for selected technologies; not a population survey.' : 'No dated, attributable local stance for the selected technologies was established. Regional context and missing coverage do not establish neutrality.',
    monthly: [...months].sort(([a], [b]) => a.localeCompare(b)).map(([month, rows]) => ({ month, count: rows.length, supportIndex: index(rows) })),
  };
}

export function recordSentimentHistory(section: PublicSentimentSection, prior: PublicSentimentSection | undefined, sourceSedId?: string): PublicSentimentSection {
  if (!prior || prior.fetchedAt === section.fetchedAt) return section;
  const oldUrls = new Set(prior.entries.map(row => row.url));
  const summary = { fetchedAt: prior.fetchedAt, sourceSedId, queryFingerprint: prior.queryContext?.fingerprint,
    entryCount: prior.entries.length, assessment: prior.assessment };
  return { ...section, history: [...(prior.history ?? []), summary].slice(-24),
    change: { addedObservationCount: section.entries.filter(row => !oldUrls.has(row.url)).length,
      comparable: prior.queryContext?.fingerprint === section.queryContext?.fingerprint,
      ...(prior.queryContext?.fingerprint === section.queryContext?.fingerprint && prior.assessment?.supportIndex !== undefined && section.assessment?.supportIndex !== undefined
        ? { supportIndexDelta: section.assessment.supportIndex - prior.assessment.supportIndex } : {}) } };
}
